// src/app/article/(BottomBar)/BottomBarClient.tsx
'use client';

import type React from 'react';
import style from './BottomBarClient.module.scss';
import { useSearchParams } from 'next/navigation';
import { useState, useCallback, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useAppSelector } from '@/store/hook';

// icon
import { IconSend } from '@/components/icons';

// 元件
import Avatar from '@/components/ui/avatar/Avatar';
// import { GiveCoinBtnInArticle } from '../(CoinGameActionBtnInArticle)/CoinGameActionBtnInArticle';
// import StatusCardModal, { type StatusKind } from '@/components/ui/modal/StatusCardModal';

// const
import { DEFAULT_AVATAR } from '@/constants/image';
import Coin from '@/components/ui/coin/Coin';

export type ReplyTarget = {
  /** 要回覆的留言 id（對應後端 message 的 id） */
  id: string;
  /** 顯示用：被回覆者的名稱 */
  authorName?: string;
  /** 這串 thread 的最上層父留言 id（用來重抓該串回覆用） */
  threadRootId?: string;
};

type ArticleReplyBarProps = {
  articleId?: string;
  /** 是否顯示右側「領取/Modal」區塊；預設 true（目前 UI 尚未啟用） */
  showClaimActions?: boolean;

  /**
   * 送出留言時呼叫的 callback
   * - content: 輸入內容
   * - parentId: 若有回覆特定留言，會帶入該留言 id；若為新增父留言則為 undefined
   */
  onSubmit?: (content: string, parentId?: string) => Promise<void> | void;

  /**
   * 目前是否正在「回覆某則留言」
   * - 若有值，會在輸入框上方顯示「回覆 @某人」
   */
  replyingTo?: ReplyTarget | null;

  /** 使用者按「取消回覆」時要做的事（例如清空 replyingTo 狀態） */
  onCancelReply?: () => void;
};

export const ArticleReplyBar = ({
  articleId,
  // showClaimActions = true, // 目前沒有啟用右側領取區，所以不解構以避免 unused
  onSubmit,
  replyingTo,
  onCancelReply,
}: ArticleReplyBarProps) => {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);

  // StatusCardModal：用專屬的開關與狀態（領取籌碼用，跟留言無關）
  // const [statusOpen, setStatusOpen] = useState(false);
  // const [statusKind, setStatusKind] = useState<StatusKind>('success');
  // const openStatus = (kind: StatusKind) => {
  //   setStatusKind(kind);
  //   setStatusOpen(true);
  // };

  // const leftColClass = useMemo(() => {
  //   if (isInputFocused) return 'col-11';
  //   return showClaimActions ? 'col-8' : 'col-11';
  // }, [isInputFocused, showClaimActions]);

  // 使用 ref 來追蹤是否已經執行過自動聚焦，避免重複執行
  const searchParams = useSearchParams();
  const lastProcessedIdRef = useRef<string | null>(null);

  const meInfo = useAppSelector((state) => state.auth.meInfo);

  // ⭐ 為了避免 hydration mismatch：首次 hydration 一律當成「未登入」
  const [mounted, setMounted] = useState(false);
  // SSR + 第一次 client render 時 user 一律為 null

  useEffect(() => {
    setMounted(true);
  }, []);

  const user = mounted ? meInfo : null;
  const [showCoinHint, setShowCoinHint] = useState(false);
  const [hintCoinSrc, setHintCoinSrc] = useState<string | null>(null);
  const [hintCoinType, setHintCoinType] = useState<string | null>(null);

  // 用來追蹤上一次處理過的時間戳記，避免重複觸發
  const lastTimestampRef = useRef<string | null>(null);
  const [tick, setTick] = useState(0); //

  // 監控 mounted 與 user 狀態，一旦準備好就檢查是否需要 Focus
  useEffect(() => {
    setMounted(true);
    // ⭐ 監聽「Session 更新」事件
    const handleUpdate = () => setTick((t) => t + 1);
    window.addEventListener('session-updated', handleUpdate);
    return () => window.removeEventListener('session-updated', handleUpdate);
  }, []);

  useEffect(() => {
    if (mounted && user && articleId) {
      const rawData = window.sessionStorage.getItem('focusReplyForArticle');
      if (!rawData) return;

      try {
        const data = JSON.parse(rawData);
        if (data.articleId === articleId) {
          // A. 如果正在回覆，切換回留言模式
          if (replyingTo && onCancelReply) {
            onCancelReply();
          }

          // B. 顯示提示
          setShowCoinHint(true);
          setHintCoinSrc(data.coinAvatarSrc);
          setHintCoinType(data.coinType);

          // C. 執行 Focus
          let attempts = 0;
          const tryFocus = () => {
            const el = document.getElementById('reply') as HTMLTextAreaElement | null;
            if (el && !el.disabled) {
              el.focus();
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // 成功後清除
              window.sessionStorage.removeItem('focusReplyForArticle');
            } else if (attempts < 15) {
              attempts++;
              setTimeout(tryFocus, 100);
            }
          };
          tryFocus();
        }
      } catch (err) {
        console.error(err);
      }
    }
  }, [mounted, !!user, articleId, replyingTo, tick]);

  const handleCancelHint = () => {
    setShowCoinHint(false);
    // 清理網址，把參數拿掉，只保留路徑
    window.history.replaceState({}, '', window.location.pathname);
    // 同時重置 Ref，確保下一次從列表點進來能再觸發
    lastTimestampRef.current = null;
  };

  const handleSend = useCallback(async () => {
    const trimmed = value.trim();

    // 沒有文字或沒有提供 onSubmit 就不做事
    if (!trimmed || !onSubmit) return;
    if (sending) return;

    try {
      setSending(true);
      await onSubmit(trimmed, replyingTo?.id);
      // 送出成功後清空輸入框 & 取消「回覆模式」
      setValue('');
      onCancelReply?.();
    } finally {
      setSending(false);
    }
  }, [value, onSubmit, replyingTo?.id, onCancelReply, sending]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    // Enter 送出，Shift+Enter 換行
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  // 是否「可以送出」的統一判斷：要有登入 + 有文字 + 有 onSubmit + 非 sending 中
  const canSend = !!user && !!value.trim() && !!onSubmit && !sending;

  return (
    <div className="row align-items-center gx-1 gy-0">
      {/* 左側：頭像 + 輸入框（＋回覆提示） */}
      <div className="d-flex flex-column">
        {/* 上方「回覆 @某人」提示列 */}
        {/* {replyingTo && (
          <div className="d-flex align-items-center mb-1 fs-sm-2 text-muted">
            <span>回覆 </span>
            <span className="ms-1 fw-bold">
              {replyingTo.authorName ? `@${replyingTo.authorName}` : '該留言'}
            </span>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-2 text-muted"
              onClick={onCancelReply}
            >
              取消
            </button>
          </div>
        )} */}

        {/* 優先顯示回覆對象，若無回覆對象則顯示籌碼提醒 */}
        {replyingTo ? (
          // 模式 A：回覆某人
          <div className="d-flex align-items-center mb-1 fs-sm-1 text-muted">
            <span>回覆 </span>
            <span className="ms-1 fw-bold text-brand">
              {replyingTo.authorName ? `@${replyingTo.authorName}` : '該留言'}
            </span>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-2 text-muted"
              onClick={onCancelReply}
            >
              取消
            </button>
          </div>
        ) : showCoinHint ? (
          // 模式 B：領取籌碼提醒
          <div className="d-flex align-items-center mb-1 fs-sm-1 text-muted">
            <span className="me-2">趕緊留言得</span>
            {hintCoinType === 'womo' ? (
              <Avatar src={hintCoinSrc || ''} size={24} />
            ) : (
              <Coin scale={0.25} color={hintCoinType === 'kol_pay' ? 'red' : 'green'}>
                <Avatar src={hintCoinSrc || ''} />
              </Coin>
            )}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-2 text-muted"
              onClick={() => setShowCoinHint(false)}
            >
              取消
            </button>
          </div>
        ) : null}

        <div className="d-flex align-items-center position-relative">
          <Link href="#">
            <Avatar src={user?.u_photo ?? DEFAULT_AVATAR} size={40} />
          </Link>
          <div className="flex-grow-1 px-2">
            <textarea
              className={`form-control ${style.reply} ${!user ? 'btn-disabled' : ''}`}
              id="reply"
              placeholder={!user ? '請先登入才能留言' : '我想說...'}
              //onFocus={() => setIsInputFocused(true)}
              //onBlur={() => setIsInputFocused(false)}
              rows={1}
              value={value}
              // onChange={(e) => setValue(e.target.value)}
              onChange={(e) => {
                setValue(e.target.value);
                // 使用者一開始打字，就隱藏籌碼提醒（選做，增加體驗）
                //if (showCoinHint) setShowCoinHint(false);
              }}
              onKeyDown={handleKeyDown}
              disabled={!user}
            />
          </div>
          <div className="text-center">
            <button
              type="button"
              className={`btn btn-outline-secondary ${!canSend ? 'btn-disabled' : ''}`}
              onClick={() => void handleSend()}
              disabled={!canSend}
            >
              <IconSend width={24} />
            </button>
          </div>
        </div>
      </div>

      {/* 右側領取區：目前整段是註解，維持你原本的寫法即可 */}
      {/* {isInputFocused ? (
        <div className="col-1 text-center">
          <button
            type="button"
            className="text-secondary"
            onClick={() => void handleSend()}
            disabled={sending || !value.trim() || !onSubmit}
          >
            <IconSend width={24} />
          </button>
        </div>
      ) : showClaimActions ? (
        <div className="col-4">
          <GiveCoinBtnInArticle
            onClick={() => openStatus('success')}
            // loading={loading}
            label="領取"
            className="me-2"
            coinColor="green"
          />

          <StatusCardModal
            open={statusOpen}
            onClose={() => setStatusOpen(false)}
            status={statusKind}
            color="green"
            avatarSrc="https://womophoto.s3.amazonaws.com/womo/p/store/RNt5WXyCBlU6=BL_1748398412.2"
          />
        </div>
      ) : (
        <div className="col-1 text-center">
          <button
            type="button"
            className="text-secondary"
            onClick={() => void handleSend()}
            disabled={sending || !value.trim() || !onSubmit}
          >
            <IconSend width={24} />
          </button>
        </div>
      )} */}
    </div>
  );
};
